/**
 * Bot Detection Utility
 *
 * Detects bots, crawlers, and data center traffic to prevent analytics pollution
 * Filters out traffic from known data centers (Boardman, etc.) and bot user agents
 */

/**
 * Known data center cities/regions that generate bot traffic
 * These show up in analytics but are not real users
 */
const DATA_CENTER_LOCATIONS = [
  'boardman', // AWS/Google data center in Oregon
  'ashburn', // Major AWS data center in Virginia
  'council bluffs', // Google data center in Iowa
  'the dalles', // Google data center in Oregon
  'pryor', // Google data center in Oklahoma
  'lenoir', // Google data center in North Carolina
  'berkeley county', // Google data center in South Carolina
  'douglas county', // Data centers in various states
  'santa clara', // Major tech hub with lots of crawlers
  'sunnyvale', // Major tech hub
  'mountain view', // Google HQ
  'menlo park', // Facebook HQ
  'redmond', // Microsoft HQ
  'seattle', // Amazon HQ
  'quincy', // Microsoft data center in Washington
  'des moines', // Microsoft data center in Iowa
  'san antonio', // Microsoft data center in Texas
  'boydton', // Microsoft data center in Virginia
  'dublin', // Major EU data center hub
  'frankfurt', // Major EU data center hub
  'amsterdam', // Major EU data center hub
  'singapore', // Major APAC data center hub
  'tokyo', // Major APAC data center hub
  'sydney', // Major APAC data center hub
];

/**
 * Bot/crawler user agent patterns
 * Comprehensive list of known bots and crawlers
 */
const BOT_USER_AGENT_PATTERNS = [
  // Search engine bots
  /googlebot/i,
  /bingbot/i,
  /slurp/i, // Yahoo
  /duckduckbot/i,
  /baiduspider/i,
  /yandexbot/i,
  /sogou/i,
  /exabot/i,

  // Social media crawlers
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /pinterestbot/i,
  /whatsapp/i,
  /telegrambot/i,
  /slackbot/i,
  /discordbot/i,

  // SEO/monitoring tools
  /ahrefsbot/i,
  /semrushbot/i,
  /mj12bot/i,
  /dotbot/i,
  /rogerbot/i,
  /screaming frog/i,
  /sitebulb/i,

  // Generic bot patterns
  /bot[\s_-]/i,
  /crawler/i,
  /spider/i,
  /scraper/i,
  /curl/i,
  /wget/i,
  /python-requests/i,
  /http-client/i,
  /java\//i,
  /okhttp/i,
  /go-http-client/i,

  // Headless browsers (often used by bots)
  /headlesschrome/i,
  /phantomjs/i,
  /selenium/i,
  /puppeteer/i,
  /playwright/i,

  // Monitoring/uptime services
  /pingdom/i,
  /uptimerobot/i,
  /statuscake/i,
  /newrelic/i,
  /datadog/i,

  // Other known bots
  /archive.org_bot/i,
  /ia_archiver/i,
  /wayback/i,
  /prerender/i,
  /lighthouse/i,
  /pagespeed/i,
  /gtmetrix/i,
];

/**
 * Check if user agent matches known bot patterns
 * @returns {boolean} True if bot detected
 */
const isBotUserAgent = () => {
  try {
    const userAgent = navigator.userAgent || '';

    // Exclude known legitimate apps from bot detection
    if (userAgent.toLowerCase().includes('madnesstime')) {
      console.debug('[Bot Detection] Madness Time app detected - not a bot');
      return false;
    }

    // Check against all bot patterns
    return BOT_USER_AGENT_PATTERNS.some((pattern) => pattern.test(userAgent));
  } catch (error) {
    console.debug('Error checking user agent:', error);
    return false;
  }
};

/**
 * Check if running in an in-app web view
 * @returns {boolean} True if in web view
 */
const isInWebView = () => {
  try {
    const userAgent = navigator.userAgent.toLowerCase();

    // Check for common web view indicators
    return (
      // iOS web view
      userAgent.includes('wv') ||
      // Android web view
      (userAgent.includes('version') &&
        userAgent.includes('mobile') &&
        !userAgent.includes('safari')) ||
      // Custom web view indicators
      userAgent.includes('madnesstime') ||
      // iOS app web view
      (userAgent.includes('applewebkit') && !userAgent.includes('safari')) ||
      // Android app web view
      (userAgent.includes('android') && userAgent.includes('wv')) ||
      // React Native
      userAgent.includes('reactnative') ||
      // Cordova/PhoneGap
      userAgent.includes('cordova') ||
      // Ionic
      userAgent.includes('ionic') ||
      // Flutter
      userAgent.includes('flutter') ||
      // Generic app indicators
      window.webkit?.messageHandlers ||
      window.Android ||
      window.webkit
    );
  } catch (error) {
    console.debug('Error checking web view:', error);
    return false;
  }
};

/**
 * Check if browser has bot-like characteristics
 * @returns {boolean} True if bot characteristics detected
 */
const hasBotCharacteristics = () => {
  try {
    // Skip bot detection for in-app web views
    if (isInWebView()) {
      console.debug(
        '[Bot Detection] In-app web view detected - skipping bot checks'
      );
      return false;
    }

    // Headless browser detection
    if (navigator.webdriver) {
      return true;
    }

    // Check for missing properties that real browsers have
    // But be more lenient for mobile/web view environments
    if (!navigator.languages || navigator.languages.length === 0) {
      // Check if this is a legitimate mobile environment
      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          navigator.userAgent
        );
      if (!isMobile) {
        return true;
      }
    }

    // Check for phantom/headless indicators
    if (window.callPhantom || window._phantom || window.phantom) {
      return true;
    }

    // Check for automation indicators
    if (window.domAutomation || window.domAutomationController) {
      return true;
    }

    // Chrome headless detection
    if (navigator.userAgent.includes('HeadlessChrome')) {
      return true;
    }

    // Check for missing plugins (real browsers usually have some)
    if (navigator.plugins && navigator.plugins.length === 0) {
      // Mobile browsers legitimately have no plugins
      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          navigator.userAgent
        );
      if (!isMobile) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.debug('Error checking bot characteristics:', error);
    return false;
  }
};

/**
 * Check if location matches known data center
 * This requires geolocation data to be available
 * @param {Object} locationData - Optional location data {city, region, country}
 * @returns {boolean} True if data center location detected
 */
const isDataCenterLocation = (locationData = null) => {
  try {
    // If location data is provided, check it
    if (locationData) {
      const city = (locationData.city || '').toLowerCase();
      const region = (locationData.region || '').toLowerCase();

      // Check if city or region matches known data centers
      return DATA_CENTER_LOCATIONS.some(
        (dcLocation) => city.includes(dcLocation) || region.includes(dcLocation)
      );
    }

    // If no location data provided, we can't determine
    // Return false to be safe (don't block without evidence)
    return false;
  } catch (error) {
    console.debug('Error checking data center location:', error);
    return false;
  }
};

/**
 * Main bot detection function
 * Combines all detection methods
 * @param {Object} options - Optional configuration
 * @param {Object} options.locationData - Optional location data from API
 * @returns {boolean} True if bot/crawler detected
 */
export const isBot = (options = {}) => {
  try {
    // Skip bot detection for in-app web views
    if (isInWebView()) {
      console.debug('[Bot Detection] In-app web view detected - not a bot');
      return false;
    }

    // Check user agent patterns
    if (isBotUserAgent()) {
      console.debug('[Bot Detection] Bot user agent detected');
      return true;
    }

    // Check browser characteristics
    if (hasBotCharacteristics()) {
      console.debug('[Bot Detection] Bot characteristics detected');
      return true;
    }

    // Check data center location if provided
    if (options.locationData && isDataCenterLocation(options.locationData)) {
      console.debug(
        '[Bot Detection] Data center location detected:',
        options.locationData
      );
      return true;
    }

    return false;
  } catch (error) {
    console.error('[Bot Detection] Error during bot detection:', error);
    // If detection fails, assume not a bot to avoid blocking real users
    return false;
  }
};

/**
 * Get detailed bot detection info for debugging
 * @param {Object} options - Optional configuration
 * @returns {Object} Detection results
 */
export const getBotDetectionInfo = (options = {}) => ({
  isBot: isBot(options),
  isBotUserAgent: isBotUserAgent(),
  hasBotCharacteristics: hasBotCharacteristics(),
  isDataCenterLocation: isDataCenterLocation(options.locationData),
  isInWebView: isInWebView(),
  userAgent: navigator.userAgent,
  locationData: options.locationData || null,
});

export default {
  isBot,
  getBotDetectionInfo,
  DATA_CENTER_LOCATIONS,
};
